import { ConfirmDialog } from 'components'
import { cn } from 'lib/utils'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { BorderTravelOverlay } from '../previews/ShimmerOverlay'
import { AssetMedia, type MediaSource } from './AssetMedia'
import { EditsApplyingBadge } from './EditsApplyingBadge'
import {
  PostCardApproveButton,
  PostCardFailedChip,
  PostCardHeldChip,
  PostCardHoverScrim,
  PostCardLiveChip,
  PostCardMenu,
  PostCardPlatformRow,
  PostCardShapeBadge,
  PostCardYoursChip,
  SHAPE_ICON,
  type FailedPlatform,
} from './post-card-chips'
import type { ContentShape } from '../store/idea-store'

/**
 * The canonical scheduled-post tile shared by the Weekly Cockpit
 * (`QueuedPostCard`) and the Calendar (`CalendarPopoverCard`) — the two surfaces
 * that render a queued/published post as a clickable media tile. Aligned with
 * the Home `IdeaCard`: a 1:1 asset (routed through `AssetMedia` so reels
 * autoplay inline + mute chip and carousels swipe) carrying two overlaid corner
 * chips — the shape badge (top-left) and, once published, the live indicator
 * (top-right) — above a platform row that also hosts the ••• menu (Edit +
 * Delete on pre-publish posts). A `needs_approval` post gets an acid Approve
 * footer. The whole card opens `PostModal` (schedule / approve / view-live).
 *
 * Purely presentational: each surface owns a thin container that maps its store
 * entity → these props and wires the side-effects (approve / delete / navigate /
 * analytics). The container computes the derived booleans; this card just draws.
 * The card is width-agnostic (`w-full`) — callers own the width.
 */
export interface ScheduledPostCardProps {
  /** Stable test hook — `queued-post-card` (cockpit) / `calendar-post-card`. */
  testId: string
  /** Media to render, or null for the empty / still-generating state. */
  mediaSource: MediaSource | null
  /** The post's declared shape — drives the corner badge. */
  shape: ContentShape
  /** Shape `AssetMedia` actually renders (downgraded to image when rich media absent). */
  effectiveShape: ContentShape
  /** Platform brand icons shown in the row below the asset. */
  platforms: string[]

  isGenerating: boolean
  isPublished: boolean
  isFailed: boolean
  /** Review-mode post awaiting sign-off — shows the acid Approve footer. */
  isNeedsApproval: boolean
  /** Whether the ••• (Edit + Delete) menu is available. */
  showMenu: boolean
  /** Whole-card click opens PostModal when true. */
  isClickable: boolean
  /** True while the parent loads this post's PostModal payload (shows a veil). */
  opening?: boolean

  /** Opens PostModal (only called when `isClickable`). */
  onOpen?: () => void
  /** Routes to the post-aware Composer (the ••• Edit action). */
  onEdit: () => void
  /**
   * Moves a scheduled post back to a draft (the ••• Unschedule action). Omit on
   * non-scheduled posts — the menu item renders only when this is provided.
   */
  onUnschedule?: () => void
  /** The actual delete — runs when the confirm dialog is accepted. */
  onConfirmDelete: () => Promise<void> | void
  /** Approve a review-mode post; omit when not approvable. */
  onApprove?: () => Promise<void> | void
  /**
   * Regenerate a post whose GENERATION terminally failed (mediaSource.failed).
   * Wired to AssetMedia's failed-media Retry button — the same treatment the
   * Home/Posts idea cards use. Omit when the surface has no retry lane (the
   * plate then renders without the button).
   */
  onRetryGeneration?: () => void

  deleteDialogTitle: string
  deleteDialogDescription: string

  /**
   * Failed siblings on this post. Drives the destructive corner chip
   * (`PostCardFailedChip`) + per-icon destructive dot on the platform row.
   * Empty/absent = no failure treatment.
   */
  failedPlatforms?: FailedPlatform[]
  /**
   * Platforms already published — acid success dot on the icon row so a
   * partially-published post reads green-vs-red at a glance.
   */
  publishedPlatforms?: string[]
  /** Disables the menu trigger + Approve while a parent action is pending (cockpit). */
  busy?: boolean
  /**
   * Index in the parent's card list. When present, the article becomes
   * keyboard-navigable (DayDetail wires j/k/←/→ + Enter/Space via `data-card-index`).
   */
  cardIndex?: number
  /** Status attribute for keyboard-nav / e2e selectors (cockpit). */
  dataStatus?: string
  /** One-shot acid pulse the moment a scheduled post goes live (cockpit). */
  isPulsing?: boolean
  /**
   * The thumbnail is the last-RENDERED master while the user's composition
   * edits are still being applied server-side — overlays the "Applying your
   * edits" chip so the stale image never reads as a revert. Callers gate this
   * on pre-publish posts (published masters are final).
   */
  isEditsApplying?: boolean
  /**
   * The creative the edits chip speaks for. Lets the chip read the live
   * edit-pipeline state (applying vs failed) from the stores and offer Retry
   * when the master render reached its terminal failure.
   */
  editsCreativeId?: string
  /**
   * Explicit "the render failed" override for surfaces whose creative is not
   * hydrated in a store (the weekly cockpit reads a server-computed flag).
   */
  editsRenderFailed?: boolean
  /**
   * Bring-your-own-content post (mediaOrigin === 'user') — shows the "Yours"
   * corner chip so brought media reads distinctly from generated. Suppressed
   * once published (the Live chip owns the corner) or failed (the failed chip
   * does).
   */
  isUserUpload?: boolean
  /**
   * Server-side publish hold ("Missed approval" / "On hold"): renders the
   * amber held chip in the top-right corner. Failed and Live chips outrank it
   * — a hold only matters while the post is silently NOT publishing.
   */
  holdLabel?: string
  /** Tooltip for the held chip: why it's held + the way out. */
  holdTitle?: string
}

export function ScheduledPostCard({
  testId,
  mediaSource,
  shape,
  effectiveShape,
  platforms,
  isGenerating,
  isPublished,
  isFailed,
  isNeedsApproval,
  showMenu,
  isClickable,
  opening,
  onOpen,
  onEdit,
  onUnschedule,
  onConfirmDelete,
  onApprove,
  onRetryGeneration,
  deleteDialogTitle,
  deleteDialogDescription,
  failedPlatforms,
  publishedPlatforms,
  busy,
  cardIndex,
  dataStatus,
  isPulsing,
  isEditsApplying,
  editsCreativeId,
  editsRenderFailed,
  isUserUpload,
  holdLabel,
  holdTitle,
}: ScheduledPostCardProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const ShapeIcon = SHAPE_ICON[shape]
  const visibleFailedPlatforms: FailedPlatform[] = failedPlatforms ?? []
  const failedPlatformNames = visibleFailedPlatforms.map(fp => fp.platform)
  const visiblePublishedPlatforms = publishedPlatforms ?? []
  const hasFailedPlatforms = visibleFailedPlatforms.length > 0

  // A failed post dims the whole tile (opacity-60), but the failure chip is the
  // ONE control the user needs to reach — so it renders in a sibling overlay
  // outside the dim scope, positioned over the article's top-right corner.
  // The wrapper stays `w-full` so callers keep width-agnostic layout.
  return (
    <>
      {/* Wrapper carries `group` so PostCardFailedChip's `group-hover:` label
          expansion fires — the chip renders here as a sibling of the dimmed
          article, so it would otherwise never see the article's hover state. */}
      <div className="group relative w-full">
        <article
          data-testid={testId}
          data-status={dataStatus}
          data-just-approved={isPulsing ? 'true' : undefined}
          data-card-index={cardIndex}
          tabIndex={cardIndex !== undefined ? 0 : undefined}
          onClick={isClickable ? onOpen : undefined}
          className={cn(
            'group relative flex w-full flex-col overflow-hidden rounded-[12px] border border-line bg-bg-2 transition-colors hover:border-line-bright',
            isClickable && 'cursor-pointer',
            cardIndex !== undefined &&
              'focus-visible:shadow-[0_0_0_2px_var(--acid),0_0_0_5px_color-mix(in_srgb,var(--acid)_22%,transparent)] focus-visible:outline-none',
            isFailed && 'opacity-60'
          )}
        >
          {/* 1:1 asset with overlaid corner chips (matches IdeaCard's tile). */}
          <div className="relative aspect-square w-full overflow-hidden bg-bg-3">
            {mediaSource ? (
              <AssetMedia
                media={mediaSource}
                shape={effectiveShape}
                hideCarouselArrows
                onRetry={onRetryGeneration}
                // Compact tile: the concession alone; the refund clause wraps
                // into a wall of text at this width (Matan, 2026-08-30).
                failedCopy="short"
                className="absolute inset-0 h-full w-full"
              />
            ) : isGenerating ? null : (
              <div className="absolute inset-0 flex items-center justify-center">
                <ShapeIcon size={36} className="text-paper-faint" />
              </div>
            )}

            <PostCardHoverScrim edge="top" />

            <PostCardShapeBadge shape={shape} iconOnly />

            {/* "Yours" chip — top-right notch on bring-your-own-content posts.
              Once published the Live chip wins the corner (and the failed
              chip when anything failed), so it renders pre-publish only. */}
            {isUserUpload && !isPublished && !hasFailedPlatforms && <PostCardYoursChip />}

            {/* Live indicator — top-right, only once published AND nothing
              failed. A partial failure (some siblings live, some failed) is
              an actionable state, so the failed chip owns the corner instead —
              a green "live" dot next to a red failed chip reads as a
              contradiction. Per-platform dots on the row still show which
              siblings published. */}
            {isPublished && !hasFailedPlatforms && (
              <div className="absolute top-2 right-2 z-20">
                <PostCardLiveChip />
              </div>
            )}

            {/* The thumbnail lags the user's composition edits until the master
              re-renders — say so instead of looking like a silent revert. */}
            {isEditsApplying && !isGenerating && !!mediaSource && (
              <EditsApplyingBadge overlay creativeId={editsCreativeId} failed={editsRenderFailed} />
            )}

            {/* Generating shimmer (owns the whole tile while drafting). */}
            {isGenerating && <BorderTravelOverlay />}

            {/* Brief loading veil while the parent fetches this post's PostModal
              payload (loadIdea round-trip). */}
            {opening && (
              <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/40">
                <Loader2 size={20} className="animate-spin text-white" />
              </div>
            )}
          </div>

          {/* Body: platform row (left) + ••• menu (right) + (review-mode) Approve.
            Edit + Delete on pre-publish posts; published posts show none (visit
            via the PostModal the card opens). */}
          <div className="flex flex-1 flex-col gap-2 px-2.5 py-2">
            {(platforms.length > 0 || showMenu) && (
              <PostCardPlatformRow
                platforms={platforms}
                failedPlatforms={failedPlatformNames}
                publishedPlatforms={visiblePublishedPlatforms}
                action={
                  showMenu ? (
                    <PostCardMenu
                      disabled={busy}
                      onEdit={onEdit}
                      onUnschedule={onUnschedule}
                      onDelete={() => setShowDeleteConfirm(true)}
                    />
                  ) : undefined
                }
              />
            )}

            {isNeedsApproval && onApprove && (
              <PostCardApproveButton onApprove={onApprove} disabled={busy} />
            )}
          </div>
        </article>

        {/* Failure chip sits OUTSIDE the dimmed article so it stays fully legible
          on a failed (opacity-60) tile. It OWNS the top-right corner — the Live
          chip is suppressed whenever anything failed (see above), so the two
          never stack. Being outside the article it misses the card's onClick,
          so it opens the same PostModal explicitly. */}
        {hasFailedPlatforms && (
          <div className="absolute top-2 right-2 z-30">
            <PostCardFailedChip
              failedPlatforms={visibleFailedPlatforms}
              onOpenPost={isClickable ? onOpen : undefined}
            />
          </div>
        )}

        {/* Held chip — the honest state for a silently-held post. Failed and
          Live own the corner first; a hold is only worth flagging while the
          post is neither. Sits outside the article like the failed chip so it
          stays legible and clickable. */}
        {holdLabel && !hasFailedPlatforms && !isPublished && (
          <div className="absolute top-2 right-2 z-30">
            <PostCardHeldChip
              label={holdLabel}
              title={holdTitle}
              onOpenPost={isClickable ? onOpen : undefined}
            />
          </div>
        )}
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title={deleteDialogTitle}
        description={deleteDialogDescription}
        onConfirm={onConfirmDelete}
      />
    </>
  )
}
